import { useEffect, useState, useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import { API, ShareAPI } from '../../API';
import { useTranslation } from 'react-i18next';
import { Loader } from '../Loader/Loader';
import { WidgetContentModal } from '../WidgetContentModal/WidgetContentModal';
import { AddToNarrativeModal } from '../AddToNarrativeModal/AddToNarrativeModal';
import { AddToSourceGroupModal } from '../AddToSourceGroupModal/AddToSourceGroupModal';
import { ReactComponent as InfoIcon} from '../../assets/anchor.svg';
import { Tooltip } from 'react-tooltip';
import {
  languageNamesMapping,
  languageNamesMappingUkrainian,
} from '../../utils/languages';
import { formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import { showRequestError } from '../../utils/requestError';
import { ECHARTS_TOOLTIP_STYLE, escapeHtml } from '../CustomDashboard/WidgetRenderer/echartsUtils';

// echarts SVG text cannot read CSS variables, so the palette is mirrored here.
const BAR_COLOR = '#2463EB';
const OTHER_BAR_COLOR = '#9AA5B4';
const AXIS_TEXT_COLOR = '#4f546c'; // --clr-gray500
const VALUE_TEXT_COLOR = '#151419'; // --main-color
const GRID_LINE_COLOR = '#f0f0f0';

export const LanguagesDistribution = ({
  narrative,
  isShare,
  isClickable = true,
  title,
}) => {
  const { t } = useTranslation();
  const [languages, setLanguages] = useState(null);
  const [modal, setModal] = useState({
    isVisible: false,
    countryCode: null,
  });

  const [addToCaseModal, setAddToCaseModal] = useState({
    isOpen: false,
    languages: null,
    exclude: false,
    sources: null,
  });
  const [addToGroupModal, setAddToGroupModal] = useState({
    isOpen: false,
    languages: null,
    exclude: false,
    sources: null,
  });

  const api = isShare ? ShareAPI : API;

  const getNarrativeLanguages = () => {
    const urlParams = new URLSearchParams();
    urlParams.set('widget_name', `languages_distribution_chart`);
    api
      .fetch(
        'GET',
        `/API/v2/narrative-widgets/${
          narrative.id
        }/languages?${urlParams.toString()}`,
      )
      .then((data) => setLanguages(data))
      .catch((e) => { showRequestError(e, t('Languages distribution')); setLanguages([]); });
  };

  const languagesCodesList = () => {
    let codesList = languages?.objects
      ?.filter((lang) => lang.code !== 'Others')
      .map((language) => language.code);

    return codesList;
  };

  useEffect(() => {
    getNarrativeLanguages();
  }, [narrative.id]);

  let userLanguage = 'en';

  if (
    localStorage.getItem('i18nextLng') === 'ua-UK' ||
    localStorage.getItem('i18nextLng') === 'ru-RU' ||
    localStorage.getItem('i18nextLng') === 'ru' ||
    localStorage.getItem('i18nextLng') === 'uk'
  ) {
    userLanguage = 'uk';
  } else {
    userLanguage = 'en';
  }

  const languageNamesMappingToUse =
    userLanguage === 'uk'
      ? languageNamesMappingUkrainian
      : languageNamesMapping;

  const chartItems = useMemo(() => {
    const objects = languages?.objects ?? [];
    if (objects.length === 0) return [];
    const totalCount = objects.reduce((sum, item) => sum + item.messages_count, 0);
    return objects.map((language) => ({
      code: language.code,
      name:
        language.code === 'Others'
          ? t('Other')
          : languageNamesMappingToUse[language.code] || language.code,
      percent: totalCount === 0 ? 0 : Number(((language.messages_count / totalCount) * 100).toFixed(1)),
      count: language.messages_count,
    }));
  }, [languages, languageNamesMappingToUse, t]);

  const chartDataLength = chartItems.length;
  let chartHeight = 450;
  if (chartDataLength > 15) {
    chartHeight = chartDataLength * 35 + 100;
  } else if (chartDataLength <= 5) {
    chartHeight = 250;
  }

  let barWidth = '50%';
  if (chartDataLength === 1) {
    barWidth = '15%';
  } else if (chartDataLength <= 5) {
    barWidth = '35%';
  } else if (chartDataLength <= 10) {
    barWidth = '40%';
  }

  const option = useMemo(
    () => ({
      aria: { enabled: true },
      grid: { left: 16, right: 64, top: 16, bottom: 16, containLabel: true },
      tooltip: {
        trigger: 'item',
        appendToBody: true,
        ...ECHARTS_TOOLTIP_STYLE,
        formatter: (params) => {
          const item = chartItems[params.dataIndex];
          if (!item) return '';
          return `${params.marker}${escapeHtml(item.name)}: <b>${item.percent}%</b> (${escapeHtml(
            formatWithCustomGigaBillion(item.count),
          )})`;
        },
      },
      xAxis: {
        type: 'value',
        min: 0,
        max: 100,
        interval: 10,
        axisLabel: { fontSize: 14, color: '#666', formatter: '{value}%' },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: GRID_LINE_COLOR } },
      },
      yAxis: {
        type: 'category',
        // Keep the API order top-to-bottom, as the previous chart did.
        inverse: true,
        data: chartItems.map((item) => item.name),
        axisLabel: { fontSize: 14, color: AXIS_TEXT_COLOR, width: 110, overflow: 'truncate' },
        axisLine: { lineStyle: { color: '#ccc' } },
        axisTick: { show: false },
      },
      series: [
        {
          type: 'bar',
          name: t('Publications'),
          barWidth,
          cursor: isClickable ? 'pointer' : 'default',
          data: chartItems.map((item) => {
            const color = item.code === 'Others' ? OTHER_BAR_COLOR : BAR_COLOR;
            return {
              value: item.percent,
              itemStyle: { color },
              // A same-colored stroke is drawn centered on the edge, so it grows
              // the hovered bar by 2px on every side (bars have no `scale`).
              emphasis: { itemStyle: { color, borderColor: color, borderWidth: 4 } },
            };
          }),
          label: {
            show: true,
            position: 'right',
            distance: 8,
            fontSize: 12,
            fontWeight: 'bold',
            color: VALUE_TEXT_COLOR,
            formatter: (params) => `${params.value}%`,
          },
          // No `focus` blur: dimming the other bars on every hover flickers as
          // the pointer crosses bar gaps. Highlight only the hovered bar instead.
          emphasis: {
            itemStyle: { shadowBlur: 6, shadowOffsetY: 1, shadowColor: 'rgba(21, 25, 25, 0.25)' },
          },
        },
      ],
    }),
    [chartItems, barWidth, isClickable, t],
  );

  const onEvents = useMemo(() => {
    if (!isClickable) return undefined;
    return {
      click: (params) => {
        const item = chartItems[params.dataIndex];
        if (!item) return;
        setModal({
          isVisible: true,
          countryCode: item.code,
          title: item.name,
        });
      },
    };
  }, [isClickable, chartItems]);

  return (
    <div className="report-section">
      <h3 className="trend-chart-controlls">
        {title ? title : t('Language distribution')}
        <div
          className="badge"
          data-tooltip-id="badge-tooltip"
          data-tooltip-content={t(
            'Language data is AI-detected. Minor inaccuracies may occur.',
          )}
          data-tooltip-place="right-start"
        >
          {t('AI-detected')}
          <InfoIcon />
        </div>
      </h3>
      <div className="card">
        <div className="card-body">
          {languages ? (
            languages?.objects?.length > 0 ? (
              <ReactECharts
                option={option}
                notMerge
                style={{ width: '100%', height: `${chartHeight}px` }}
                opts={{ renderer: 'svg' }}
                onEvents={onEvents}
              />
            ) : (
              <div className="noDataContainer">
                {t('There is no data to display')}
              </div>
            )
          ) : (
            <div>
              <Loader />
            </div>
          )}
        </div>
        <Tooltip id="badge-tooltip" style={{ width: '300px' }} />
      </div>

      {modal.isVisible && (
        <WidgetContentModal
          infoModal={modal.isVisible}
          isChart={true}
          onClose={() => setModal({ isVisible: false, countryCode: null, title: '' })}
          narrative={narrative}
          widgetName={`languages_distribution_chart`}
          languageCodeExclude={modal.countryCode === 'Others'}
          language={
            modal.countryCode && modal.countryCode === 'Others'
              ? languagesCodesList()
              : [modal.countryCode]
          }
          isShare={isShare}
          isClickable={false}
          title={modal.title || t('Languages')}
          showAddToActions={true}
          onChange={(bool) => {
            setModal({
              isVisible: bool,
              countryCode: null,
              title: '',
            });
          }}
          onAddToCase={(bool, sourceId) => {
            setAddToCaseModal({
              isOpen: bool,
              languages:
                modal.countryCode !== 'Others'
                  ? [modal.countryCode]
                  : languagesCodesList(),
              exclude: modal.countryCode === 'Others',
              sources: sourceId ? sourceId : null,
            });
          }}
          onAddToGroup={(bool, sourceId) => {
            setAddToGroupModal({
              isOpen: bool,
              languages:
                modal.countryCode !== 'Others'
                  ? [modal.countryCode]
                  : languagesCodesList(),
              exclude: modal.countryCode === 'Others',
              sources: sourceId ? sourceId : null,
            });
          }}
        />
      )}

      {addToCaseModal.isOpen ? (
        <AddToNarrativeModal
          isOpen={addToCaseModal.isOpen}
          deduplication={false}
          caseId={narrative.id}
          areAllMessagesSelected={true}
          languages={addToCaseModal.languages}
          languagesExclude={addToCaseModal.exclude}
          sources={addToCaseModal.sources ? [addToCaseModal.sources] : []}
          onChange={(bool) => {
            setAddToCaseModal({
              isOpen: bool,
              languages: null,
              exclude: false,
              sources: null,
            });
          }}
        />
      ) : (
        ''
      )}

      {addToGroupModal.isOpen ? (
        <AddToSourceGroupModal
          isOpen={addToGroupModal.isOpen}
          deduplication={false}
          areAllMessagesSelected={true}
          languages={addToGroupModal.languages}
          languagesExclude={addToGroupModal.exclude}
          sourcesFilter={addToGroupModal.sources ? [addToGroupModal.sources] : []}
          narrative={narrative}
          onChange={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
              languages: null,
              exclude: false,
              sources: null,
            });
          }}
        />
      ) : (
        ''
      )}
    </div>
  );
};
